import * as React from 'react';
import classNames from 'classnames';
import { useController } from 'react-hook-form';

import Icon from 'components/icon';

interface FixtureFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  /** Flow-rate fields accept free decimals (e.g. "1.0"); quantities are integers. */
  decimal?: boolean;
  /** Placeholder shown when the field is empty (0 renders as empty). */
  placeholder?: string;
}

/**
 * Parse a raw input string to its numeric value, tolerating the in-progress
 * decimal states a user types: "" and a lone "." mean 0, "1." means 1. Used by
 * both the change handler and the external-sync effect so they agree on what a
 * partial entry "means" - otherwise the effect would see "." as NaN and wipe it.
 */
const parseInput = (raw: string): number => {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '.') return 0;
  return Number(trimmed);
};

/**
 * Single numeric fixture field inside a split group. Used for both the
 * quantity column and (on Max Use facilities) the flow-rate column.
 *
 * The visible value is held as local text so a user can type an in-progress
 * decimal like "1." or "1.0" without the controlled numeric value snapping it
 * back to a whole number on each keystroke. Flow-rate fields also render as a
 * text/decimal input, because a number input sanitises away the trailing "."
 * or ".0" before we ever see it. The parsed number is what's pushed to the form.
 */
const FixtureField: React.VFC<FixtureFieldProps> = ({ name, label, disabled, decimal = false, placeholder = '0' }) => {
  const { field } = useController({ name });
  const numericValue = typeof field.value === 'number' ? field.value : 0;

  // Canonical display for a stored value: empty when 0 (so the placeholder
  // shows), and on flow-rate fields whole numbers get a trailing ".0" so a
  // GPM reads as a flow rate ("1" -> "1.0"). Fractional values are left as-is.
  const format = React.useCallback(
    (n: number): string => {
      if (n === 0) return '';
      return decimal && Number.isInteger(n) ? `${n}.0` : String(n);
    },
    [decimal]
  );

  const [text, setText] = React.useState(() => format(numericValue));

  // Re-sync the visible text when the form value changes from outside this
  // input (project hydrate / reset / start-over), but leave an in-progress
  // "1." / "1.0" alone while typing (it still parses back to the same number).
  React.useEffect(() => {
    const parsed = parseInput(text);
    if (!Number.isFinite(parsed) || parsed !== numericValue) {
      setText(format(numericValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericValue]);

  const inputId = name.replace(/\./g, '-');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    // Flow rate: allow only digits and a single decimal point, permitting the
    // in-progress "1." / "1.0" / ".5" a number input would otherwise eat.
    if (decimal && raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setText(raw);
    const parsed = parseInput(raw);
    field.onChange(Number.isFinite(parsed) ? parsed : 0);
  };

  // Normalize the display when the user leaves the field, so "1" reads "1.0".
  const handleBlur = () => {
    field.onBlur();
    setText(format(numericValue));
  };

  return (
    <div className="c-form-element__field">
      <input
        id={inputId}
        type={decimal ? 'text' : 'number'}
        inputMode={decimal ? 'decimal' : undefined}
        min={decimal ? undefined : 0}
        step={decimal ? undefined : 1}
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <label htmlFor={inputId} className="c-form-label">
        {label}
      </label>
    </div>
  );
};

/**
 * One fixture's inputs. In quantity-only mode this is a single field. When
 * `withFlowRate` is set (Max Use facilities) the quantity and a per-fixture
 * GPM field sit side by side as two columns on the same fixture row, so the
 * flow rate reads as an extra column rather than a separate stacked row.
 */
const FixtureInputs: React.VFC<{ row: FixtureRow; withFlowRate?: boolean; disabled?: boolean }> = ({
  row,
  withFlowRate,
  disabled,
}) => {
  if (!withFlowRate) {
    return <FixtureField name={`fixtures.${row.idx}.quantity`} label={row.label} disabled={disabled} />;
  }

  return (
    <div className="u-flex u-items-start" style={{ gap: '0.5rem' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <FixtureField name={`fixtures.${row.idx}.quantity`} label={row.label} disabled={disabled} />
      </div>
      <div style={{ flex: '0 0 10rem' }}>
        <FixtureField
          name={`fixtures.${row.idx}.flow_rate`}
          label="GPM"
          decimal
          placeholder="0.0"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

interface FixtureRow {
  idx: number;
  label: string;
}

interface FixtureSplitGroupProps {
  rows: ReadonlyArray<FixtureRow>;
  advancedRows?: ReadonlyArray<FixtureRow>;
  showAdvanced?: boolean;
  onShowAdvancedChange?: (show: boolean) => void;
  disabled?: boolean;
  helpText?: React.ReactNode;
  /**
   * When true, each fixture also gets a per-fixture flow-rate (GPM) field.
   * Required for Max Use facilities (gym/club/industrial/maximum flow), whose
   * demand is derived additively from fixture flow rates rather than a curve.
   */
  withFlowRate?: boolean;
}

/**
 * Fixture quantity list matching the s4 cutup's
 * `c-form-element--style-fill c-form-element--split@sm` pattern.
 */
export const FixtureSplitGroup: React.VFC<FixtureSplitGroupProps> = ({
  rows,
  advancedRows = [],
  showAdvanced = false,
  onShowAdvancedChange,
  disabled,
  helpText,
  withFlowRate = false,
}) => {
  const hasAdvanced = advancedRows.length > 0;

  return (
    <div className="o-col-12@sm u-mb-spacer-base-small">
      <div
        className={classNames(
          'c-form-element c-form-element--style-fill c-form-element--split@sm',
          hasAdvanced && 'js-form-fields-visibility',
          hasAdvanced && showAdvanced && 'is-visible'
        )}
      >
        <div className="c-form-element__field-group">
          {rows.map((row) => (
            <FixtureInputs key={row.idx} row={row} withFlowRate={withFlowRate} disabled={disabled} />
          ))}
          {showAdvanced &&
            advancedRows.map((row) => (
              <FixtureInputs key={row.idx} row={row} withFlowRate={withFlowRate} disabled={disabled} />
            ))}
        </div>
        {hasAdvanced && typeof onShowAdvancedChange === 'function' && (
          <div className="c-form-element__field-group-actions u-text-right">
            <button
              type="button"
              className="c-link-cta-ghost c-link-default u-normal-case js-form-fields-visibility-toggle has-show"
              onClick={() => onShowAdvancedChange(true)}
            >
              <Icon name="chevron" className="o-svg-icon o-svg-down o-svg-smaller" />
              <span>Show Advanced Options</span>
            </button>
            <button
              type="button"
              className="c-link-cta-ghost c-link-default u-normal-case js-form-fields-visibility-toggle has-hide"
              onClick={() => onShowAdvancedChange(false)}
            >
              <Icon name="chevron" className="o-svg-icon o-svg-up o-svg-smaller" />
              <span>Hide Advanced Options</span>
            </button>
          </div>
        )}
        {typeof helpText !== 'undefined' && <p className="c-note">{helpText}</p>}
      </div>
    </div>
  );
};

export default FixtureSplitGroup;
